import { RedirectUnAuthUser } from '@/processes'
import { ProfileTemplate } from '@/widgets'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { getCurrentLanguage } from '@/i18next'
import {
	TabUnstyled as Tab,
	TabsListUnstyled as TabsList,
	TabsUnstyled as Tabs,
	TabPanelUnstyled as TabPanel,
} from '@mui/base'
import styles from './RequirementLaborResources.module.scss'
import React, { memo, useEffect, useState } from 'react'
import {
	AgroProducerTable,
	AnimalHusbandryTable,
	CropCultivationTable,
	Badge,
	CalcAgroForm,
	CalcAnimalForm,
} from '@features/requirementLaborRecources'
import cl from 'classnames'
import { requirementLaborResourcesActions, requirementLaborResourcesThunks } from '@/entity/requirementLaborResources'
import { LoadingBlocker } from '@/components/Loader'
import { Alert } from '@/components/Alert/Alert'
import { InfoGreen } from '@app/icons'
import { Icon } from '@/components/Icon'
import { handbookThunks } from '@/entity/handbook'
import { LANGUAGE_ENUM } from '@/shared'

export const toValue = (number = 0) => {
	let [integerPart, fractionalPart] = String(number).split('.')
	integerPart = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ' ')
	return fractionalPart ? `${integerPart}.${fractionalPart}` : integerPart
}

const TAB_KEY = {
	agroProducerDto: 'agroProducerDto',
	animalHusbandryDto: 'animalHusbandryDto',
	cropCultivationDto: 'cropCultivationDto',
	calculator: 'calculator',

	calcAgro: 'calcAgro',
	calcAnimal: 'calcAnimal',
}

let cropCalcInitialState = {
	cropCode: null,
	regId: null,
	idRai: null,
	area: null,
}

let animalCalcInitialState = {
	animalKind: null,
	cnt: null,
}

let formInitial = {
	[TAB_KEY.calcAgro]: cropCalcInitialState,
	[TAB_KEY.calcAnimal]: animalCalcInitialState,
}

export const RequirementLaborResources = memo(() => {
	const { t } = useTranslation()
	const dispatch = useDispatch()
	const lang = getCurrentLanguage()

	const { dataState, animalKind, crop, calculator } = useSelector((state) => ({
		dataState: state.requirementLaborResources.data,
		animalKind: state.handbook.animalKind,
		crop: state.handbook.crop,
		calculator: state.requirementLaborResources.calculator,
	}))

	const [activeTab, setActiveTab] = useState({
		1: null,
		2: null,
	})

	const [formInitialState, setFormInitialState] = useState(formInitial)

	const isLoadData =
		dataState?.data?.agroProducerDto?.landRegistryDtos?.length ||
		dataState?.data?.agroProducerDto?.sownAreasDto?.length ||
		dataState?.data?.agroProducerDto?.livestockCountDtos?.length

	useEffect(() => {
		dispatch(requirementLaborResourcesThunks.getData())

		if (!animalKind?.length > 0) dispatch(handbookThunks.getAnimalKind())
		if (!crop?.length > 0) dispatch(handbookThunks.getCrop())
	}, [])

	useEffect(() => {
		if (dataState.data) {
			let res = TAB_KEY.agroProducerDto
			setActiveTab({
				1: res,
				2: null,
			})
		}
	}, [dataState.data])

	const handleChangeTab = (tabs) => {
		setActiveTab(tabs)
	}

	const handleSubmitCalc = (values, type) => {
		let body = {
			data: {
				params: {
					...values,
					...(type === TAB_KEY.calcAgro
						? {
								regId: values.regId,
								idRai: values.idRai,
						  }
						: {}),
				},
				payload: {
					type,
				},
			},
			onSuccess: (res) => {},
		}
		dispatch(requirementLaborResourcesThunks.getCalculator(body))
	}

	const handleResetCalc = (type) => {
		setFormInitialState((prev) => ({
			...prev,
			[type]: formInitial[type],
		}))
		dispatch(requirementLaborResourcesActions.getCalculatorActionReset(type))
	}

	const handleChangeCalc = (values, type) => {
		setFormInitialState((prev) => ({
			...prev,
			[type]: values,
		}))
	}

	const tabs = [
		{
			key: TAB_KEY.agroProducerDto,
			label: t('requirement_labor_resources.agroProducer'),
			render: isLoadData ? (
				<AgroProducerTable />
			) : (
				<Alert icon={<Icon component={InfoGreen} />} text={t('requirement_labor_resources.empty_alert')} />
			),
		},
		{
			key: TAB_KEY.cropCultivationDto,
			label: t('requirement_labor_resources.cropCultivation'),
			...(dataState.data?.cropCultivationDto
				? {
						children: dataState.data?.cropCultivationDto?.map((item, index) => {
							let label = {
								[LANGUAGE_ENUM.kz]: item.corpNameKz,
								[LANGUAGE_ENUM.rus]: item.corpNameRu,
							}
							return {
								key: `cropCultivationDto-${index}`,
								label: label[lang],
								render: (
									<div className={cl('flex', 'flex-column', 'gap-3')}>
										<div className={cl('flex', 'gap-3')}>
											<Badge value={toValue(item.sowingArea)} label={t('requirement_labor_resources.sowingArea')} />
											<Badge value={toValue(item.maxHeadCount)} label={t('requirement_labor_resources.worker_count')} />
										</div>
										<CropCultivationTable item={item} />
									</div>
								),
							}
						}),
				  }
				: {
						render: <Alert icon={<Icon component={InfoGreen} />} text={t('requirement_labor_resources.empty_alert')} />,
				  }),
		},
		{
			key: TAB_KEY.animalHusbandryDto,
			label: t('requirement_labor_resources.animalHusbandry'),
			...(dataState.data?.animalHusbandryDto
				? {
						children: dataState.data?.animalHusbandryDto?.map((item, index) => {
							let label = {
								[LANGUAGE_ENUM.kz]: item.animalsNameKz,
								[LANGUAGE_ENUM.rus]: item.animalsNameRu,
							}
							return {
								key: `animalHusbandryDto-${index}`,
								label: label[lang],
								render: (
									<div className={cl('flex', 'flex-column', 'gap-4')}>
										<div className={cl('flex', 'gap-3')}>
											<Badge label={t('requirement_labor_resources.headcount')} value={toValue(item.totalAnimalCnt)} />
											<Badge
												label={t('requirement_labor_resources.worker_count')}
												value={toValue(item.totalHeadCount)}
											/>
										</div>
										<AnimalHusbandryTable item={item} />
									</div>
								),
							}
						}),
				  }
				: {
						render: <Alert icon={<Icon component={InfoGreen} />} text={t('requirement_labor_resources.empty_alert')} />,
				  }),
		},
		{
			key: TAB_KEY.calculator,
			label: t('requirement_labor_resources.calculator'),
			children: [
				{
					key: TAB_KEY.calcAgro,
					label: t('requirement_labor_resources.cultivationCrops'),
					render: (
						<div>
							<CalcAgroForm
								handleSubmit={(values) => handleSubmitCalc(values, TAB_KEY.calcAgro)}
								handleChange={(values) => handleChangeCalc(values, TAB_KEY.calcAgro)}
								values={formInitialState.calcAgro}
								initialState={cropCalcInitialState}
								handleReset={() => handleResetCalc(TAB_KEY.calcAgro)}
							/>

							{calculator.data?.[TAB_KEY.calcAgro] && (
								<>
									{calculator.data?.[TAB_KEY.calcAgro]?.maxHeadCount && (
										<div className={'my-4'}>
											<Badge
												label={t('requirement_labor_resources.worker_count')}
												value={toValue(calculator.data?.[TAB_KEY.calcAgro]?.maxHeadCount)}
											/>
										</div>
									)}

									<CropCultivationTable item={calculator.data?.[TAB_KEY.calcAgro]} />
								</>
							)}
						</div>
					),
				},
				{
					key: TAB_KEY.calcAnimal,
					label: t('requirement_labor_resources.livestockManagement'),
					render: (
						<div>
							<CalcAnimalForm
								handleSubmit={(values) => handleSubmitCalc(values, TAB_KEY.calcAnimal)}
								handleChange={(values) => handleChangeCalc(values, TAB_KEY.calcAnimal)}
								values={formInitialState.calcAnimal}
								initialState={animalCalcInitialState}
								handleReset={() => handleResetCalc(TAB_KEY.calcAnimal)}
							/>

							{calculator.data?.[TAB_KEY.calcAnimal] && (
								<>
									{calculator.data?.[TAB_KEY.calcAnimal]?.totalHeadCount && (
										<div className={'my-4'}>
											<Badge
												label={t('requirement_labor_resources.worker_count')}
												value={toValue(calculator.data?.[TAB_KEY.calcAnimal]?.totalHeadCount)}
											/>
										</div>
									)}

									<AnimalHusbandryTable item={calculator.data?.[TAB_KEY.calcAnimal]} />
								</>
							)}
						</div>
					),
				},
			],
		},
	]

	return (
		<>
			<RedirectUnAuthUser>
				<ProfileTemplate title={'requirement_labor_resources.title'}>
					{dataState?.success && (
						<Tabs
							value={activeTab[1]}
							onChange={(e, value) =>
								handleChangeTab({
									1: value,
									2: tabs?.find((item) => item.key === value)?.children?.[0]?.key,
								})
							}
							className={styles.tabOuter}
						>
							<TabsList className={styles.tabOuter__list}>
								{tabs?.map((item) => (
									<Tab value={item?.key} key={item?.key} id={item.key} className={styles.tabOuter__item}>
										{item?.label}
									</Tab>
								))}
							</TabsList>
							{tabs?.map((item) => (
								<TabPanel key={item.key} value={item.key}>
									{item?.children ? (
										<Tabs
											value={activeTab?.[2]}
											onChange={(e, value) => handleChangeTab({ 1: activeTab[1], 2: value })}
											className={styles.tabInner}
										>
											<TabsList>
												{item.children?.map((inner) => (
													<Tab value={inner?.key} key={inner?.key} id={inner?.key} className={styles.tabInner__item}>
														{inner?.label}
													</Tab>
												))}
											</TabsList>
											{item.children?.map((inner) => (
												<TabPanel key={inner?.key} value={inner?.key}>
													<div className={styles.content_wrapper}>{inner.render}</div>
												</TabPanel>
											))}
										</Tabs>
									) : (
										item.render
									)}
								</TabPanel>
							))}
						</Tabs>
					)}
				</ProfileTemplate>
			</RedirectUnAuthUser>

			{(dataState.loading || calculator.loading) && <LoadingBlocker />}
		</>
	)
})
export default RequirementLaborResources
